import { memo } from "react";
import { CButton, CCol, CRow } from '@coreui/react';
import { FastField } from 'formik';
import Select, { components } from 'react-select';
import CIcon from '@coreui/icons-react';
import { CInvalidFeedback } from "../../../../../../migration/CInvalidFeedback";
import CFormGroup from "../../../../../../migration/CFormGroup";
import CLabel from "../../../../../../migration/CLabel";
import { CInput } from "../../../../../../migration/CInput";
import { TooltipHover } from "../../../../../../general/TooltipHover";
import { iconInfoCircle } from "../../../../../../../assets/icons/logo-negative";

const TooltipDescription = ({
    name, label, tooltipDescription
}) => {
    return (
        <div className="d-inline-flex align-items-center gap-1 mb-1">
            <CLabel htmlFor={name} className="mb-0">{label}</CLabel>

            <TooltipHover
                description={tooltipDescription}
                maxWidthContent={265}
            >
                <CIcon icon={iconInfoCircle} className="icon" style={{ '--ci-color': 'white' }} />
            </TooltipHover>
        </div>
    )
}

const FormInput = ({
    name, label, errors, values, touched, onChange, tooltipDescription, ...props
}) => (
    <CFormGroup className="mb-3">
        <TooltipDescription
            name={name}
            label={label}
            tooltipDescription={tooltipDescription}
        />

        <CInput
            id={name}
            invalid={touched && !!errors}
            value={values}
            onChange={onChange}
            {...props}
        />
        <CInvalidFeedback>{touched && errors}</CInvalidFeedback>
    </CFormGroup>
);

const FormSelect = ({
    name, label, errors, values, touched, options, onChange, className, tooltipDescription, isDisabled, isMulti = false, existedValues = [],
}) => {
    let selectedValue = values;
    if (isMulti) {
        selectedValue = Array.isArray(values)
            ? values.map(val =>
                typeof val === 'object' && val !== null
                    ? val
                    : options.find(opt => opt.value === val)
            ).filter(Boolean)
            : [];
    } else {
        selectedValue =
            typeof values === 'object' && values !== null
                ? values
                : options.find(opt => opt.value === values) || null;
    }

    const ConditionalRemove = (props) => {
        const optionValue = props.data.value;
        if (existedValues.includes(optionValue)) {
            return <span style={{ width: "6px", display: "inline-block" }} />;
        }
        return <components.MultiValueRemove {...props} />;
    };

    return (
        <CFormGroup className="mb-3">
            <TooltipDescription
                name={name}
                label={label}
                tooltipDescription={tooltipDescription}
            />

            <Select
                classNamePrefix="react-select"
                className={`${touched && !!errors ? 'is-invalid' : ''} ${className}`}
                options={options}
                placeholder="Select a field"
                value={selectedValue}
                onChange={onChange}
                isMulti={isMulti}
                isDisabled={isDisabled}
                closeMenuOnSelect={!isMulti}
                backspaceRemovesValue={false}
                components={{
                    MultiValueRemove: ConditionalRemove
                }}
            />
            <CInvalidFeedback>{touched && errors}</CInvalidFeedback>
        </CFormGroup>
    );
};

const ObjectFormRow = (
    ({
        idx,
        uniqueIdOptions,
        timestampOptions,
        isDeletedOptions,
        getOtherFieldOptions,
        selectAllOptions,
        remove,
        setFieldValue,
        showDelete,
        isImported,
        isImportedUnique,
        existedValues = [],
        allHeaders = [],
    }) => {
        return (
            <CRow className="bg-light mb-4 py-3 rounded-lg">
                <CCol lg="3">
                    <FastField name={`objects[${idx}].name`}>
                        {({ field, meta }) => (
                            <FormInput
                                name={field.name}
                                value={field.value}
                                onChange={field.onChange}
                                errors={meta.error}
                                touched={meta.touched}
                                disabled={isImported}
                                label="Object Name"
                                placeholder="Enter a name"
                                tooltipDescription="This name will be used to identify the object in your reporting."
                            />
                        )}
                    </FastField>
                </CCol>
                <CCol lg="3">
                    <FastField name={`objects[${idx}].unique_id`}>
                        {({ field, meta, form }) => {
                            return (
                                <FormSelect
                                    name={field.name}
                                    values={form.values.objects[idx].unique_id}
                                    options={uniqueIdOptions}
                                    errors={meta.error}
                                    touched={meta.touched}
                                    isMulti
                                    isDisabled={isImportedUnique}
                                    label="Unique ID"
                                    className="disabled-multiple"
                                    onChange={opt => {
                                        setFieldValue(field.name, opt);
                                    }}
                                    tooltipDescription="You must select a field that include a unique identifier for each record."
                                />
                            )
                        }}
                    </FastField>
                </CCol>
                <CCol lg="3">
                    <FastField name={`objects[${idx}].created_timestamp`}>
                        {({ field, meta, form }) => (
                            <FormSelect
                                name={field.name}
                                values={form.values.objects[idx].created_timestamp}
                                options={timestampOptions}
                                errors={meta.error}
                                touched={meta.touched}
                                label="Created Timestamp"
                                onChange={opt => setFieldValue(field.name, opt)}
                                tooltipDescription="Select the field that indicates the time when a record was created. If none is provided, we will use the first imported timestamp as the created date."
                            />
                        )}
                    </FastField>
                </CCol>
                <CCol lg="3">
                    <FastField name={`objects[${idx}].modified_timestamp`}>
                        {({ field, meta, form }) => (
                            <FormSelect
                                name={field.name}
                                values={form.values.objects[idx].modified_timestamp}
                                options={timestampOptions}
                                errors={meta.error}
                                touched={meta.touched}
                                label="Modified Timestamp"
                                onChange={opt => setFieldValue(field.name, opt)}
                                tooltipDescription="Select the field that indicates the time when the record was most recently modified. If none is provided and we detect a change, we will use the most recent imported time as the time of the modification."
                            />
                        )}
                    </FastField>
                </CCol>
                <CCol lg="3">
                    <FastField name={`objects[${idx}].is_deleted`}>
                        {({ field, meta, form }) => (
                            <FormSelect
                                name={field.name}
                                values={form.values.objects[idx].is_deleted}
                                options={isDeletedOptions}
                                errors={meta.error}
                                touched={meta.touched}
                                label="Is Deleted"
                                onChange={opt => setFieldValue(field.name, opt)}
                                tooltipDescription="Select a boolean field that will contain true when this record is deleted."
                            />
                        )}
                    </FastField>
                </CCol>
                <CCol lg="6">
                    <FastField name={`objects[${idx}].other_object_fields`}>
                        {({ field, meta, form }) => (
                            <FormSelect
                                name={field.name}
                                values={form.values.objects[idx].other_object_fields}
                                options={getOtherFieldOptions(form.values.objects[idx])}
                                isMulti
                                errors={meta.error}
                                touched={meta.touched}
                                label="Other Object Fields"
                                existedValues={existedValues}
                                onChange={opts => {
                                    const optionsList = getOtherFieldOptions(form.values.objects[idx]);
                                    if (Array.isArray(opts)) {
                                        const isSelectAll = opts.some(opt => opt.value === selectAllOptions.value);

                                        if (isSelectAll) {
                                                setFieldValue(
                                                    field.name,
                                                    optionsList.filter(opt => opt.value !== selectAllOptions.value)
                                                );
                                        } else {
                                            setFieldValue(field.name, opts);
                                        }
                                    } else {
                                        setFieldValue(field.name, []);
                                    }
                                }}
                                tooltipDescription="Select all of the other fields that should be included with the object's records."
                            />
                        )}
                    </FastField>
                </CCol>
                <CCol lg="3">
                    {showDelete && !isImportedUnique && (
                        <div>
                            <CButton
                                className="d-inline deleteicon px-0 py-0 mt-4"
                                onClick={() => remove(idx)}
                                type="button"
                            >
                                <CIcon icon="iconDeleteField" className="icon-delete" />
                            </CButton>
                        </div>
                    )}
                </CCol>
            </CRow>
        );
    }
);

export default memo(ObjectFormRow);